import { useContext } from 'react';

import {
  faChildReaching,
  faPersonSimple,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@repo/videa-components/src/index';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';

import { ExternalPatientContext } from '@/app/external/constants';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ThemeModeContext } from '../casePresentation/patientDownloadable/PreviewDialog/QRPrintDownloadContent';

import { AdultTabs } from './AdultTabs';
import { PediatricTabs } from './PediatricTabs';

enum AdultPediatricTab {
  ADULT = 'ADULT',
  PEDIATRIC = 'PEDIATRIC',
}

interface Props {
  imageId?: string;
  mode?: 'dark' | 'light';
}

export const PatientEducationContent = ({ imageId, mode = 'dark' }: Props) => {
  const { t } = useLingui();
  const { trackEvent } = useTrackEvent();
  const {
    isIframe,
    externalPatientId,
    externalImageId,
    patientId,
    trialStatus,
    centralizedBilling: isCentralizedBilling,
  } = useContext(ExternalPatientContext);
  const { id: selectedPatientId } = useParams<{ id: string }>();

  return (
    <ThemeModeContext.Provider value={mode}>
      <div className="vai-w-full vai-max-w-full vai-min-w-0 vai-overflow-hidden">
        <div className="vai-flex vai-flex-col vai-gap-[6px] vai-w-full vai-max-w-full vai-min-w-0 vai-mb-4">
          <p className="vai-font-inter vai-font-normal vai-text-sm vai-text-muted-foreground vai-w-full vai-max-w-full vai-min-w-0">
            <Trans>
              Select a treatment to present visual aids that support patient
              understanding.
            </Trans>
          </p>
        </div>
        <Tabs
          defaultValue="ADULT"
          className="vai-w-full vai-max-w-full vai-min-w-0 vai-overflow-hidden"
          onValueChange={(value) => {
            trackEvent(mixpanelEvents.patientEducationTabChange, {
              patientId: selectedPatientId ?? patientId,
              imageId,
              isIframe: !!isIframe,
              externalPatientId,
              externalImageId,
              tab: value,
              trialStatus,
              centralizedBilling: isCentralizedBilling,
            });
          }}
        >
          <TabsList
            className={classNames('vai-mb-2', {
              'vai-bg-zinc-800 vai-text-zinc-400': mode === 'dark',
              'vai-bg-zinc-100 vai-text-zinc-500': mode === 'light',
            })}
          >
            <TabsTrigger
              className={classNames(
                'vai-w-40 vai-flex vai-items-center vai-gap-2',
                {
                  'data-[state=active]:vai-bg-zinc-950 data-[state=active]:vai-text-zinc-50':
                    mode === 'dark',
                  'data-[state=active]:vai-bg-white data-[state=active]:vai-text-zinc-900':
                    mode === 'light',
                },
              )}
              value={AdultPediatricTab.ADULT}
              data-testid="patient-education-tab-adult"
            >
              <FontAwesomeIcon icon={faPersonSimple} /> {t`Adult`}
            </TabsTrigger>

            <TabsTrigger
              className={classNames(
                'vai-w-40 vai-flex vai-items-center vai-gap-2',
                {
                  'data-[state=active]:vai-bg-zinc-950 data-[state=active]:vai-text-zinc-50':
                    mode === 'dark',
                  'data-[state=active]:vai-bg-white data-[state=active]:vai-text-zinc-900':
                    mode === 'light',
                },
              )}
              value={AdultPediatricTab.PEDIATRIC}
              data-testid="patient-education-tab-pediatric"
            >
              <FontAwesomeIcon icon={faChildReaching} /> {t`Pediatric`}
            </TabsTrigger>
          </TabsList>

          <TabsContent
            value={AdultPediatricTab.ADULT}
            className="vai-w-full vai-max-w-full vai-min-w-0 vai-overflow-hidden"
          >
            <AdultTabs imageId={imageId} />
          </TabsContent>
          <TabsContent
            value={AdultPediatricTab.PEDIATRIC}
            className="vai-w-full vai-max-w-full vai-min-w-0 vai-overflow-hidden"
          >
            <PediatricTabs imageId={imageId} />
          </TabsContent>
        </Tabs>
      </div>
    </ThemeModeContext.Provider>
  );
};
